import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Divider, Row, Space, Tag, Typography, message, Spin } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RollbackOutlined,
  UndoOutlined,
  SaveOutlined,
  CalendarOutlined,
  BellOutlined,
  SlackOutlined,
} from '@ant-design/icons';
import styled, { css } from 'styled-components';
import { AccountRepositoryImpl } from '../../Modules/Account/Data/repositories/AccountRepositoryImpl';
import { GetSlackNotifySettingsUseCase } from '../../Modules/Account/Domain/UseCase/GetSlackNotifySettingsUseCase';
import { UpdateSlackNotifySettingsUseCase } from '../../Modules/Account/Domain/UseCase/UpdateSlackNotifySettingsUseCase';
import { SlackNotifyAction } from '../../Modules/Account/Model/SlackNotifySettings';
import { useAppSelector } from '../../Modules/Presentation/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';

const { Title, Text, Paragraph } = Typography;

const DEFAULT_DAYS = [0, 1, 2, 3, 4, 5, 6];
const DEFAULT_ACTIONS: SlackNotifyAction[] = [
  'create_slot',
  'delete_slot',
  'update_slot',
  'extend_slot',
  'refund_slot',
];

const DAY_OPTIONS = [
  { label: '월', value: 0 },
  { label: '화', value: 1 },
  { label: '수', value: 2 },
  { label: '목', value: 3 },
  { label: '금', value: 4 },
  { label: '토', value: 5, isWeekend: true },
  { label: '일', value: 6, isWeekend: true },
];

const ACTION_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  create_slot: { label: '슬롯 생성', icon: <PlusCircleOutlined />, color: '#52c41a' },
  delete_slot: { label: '슬롯 삭제', icon: <DeleteOutlined />, color: '#ff4d4f' },
  update_slot: { label: '슬롯 수정', icon: <EditOutlined />, color: '#1890ff' },
  extend_slot: { label: '슬롯 연장', icon: <ClockCircleOutlined />, color: '#722ed1' },
  refund_slot: { label: '슬롯 환불', icon: <RollbackOutlined />, color: '#fa8c16' },
};

const ACTION_OPTIONS: { label: string; value: SlackNotifyAction }[] = [
  { label: '슬롯 생성', value: 'create_slot' },
  { label: '슬롯 삭제', value: 'delete_slot' },
  { label: '슬롯 수정', value: 'update_slot' },
  { label: '슬롯 연장', value: 'extend_slot' },
  { label: '슬롯 환불', value: 'refund_slot' },
];

const DayButton = styled.div<{ $checked: boolean; $isWeekend?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  border: 2px solid ${({ $checked }) => ($checked ? '#1890ff' : '#d9d9d9')};
  background: ${({ $checked }) => ($checked ? '#1890ff' : '#fff')};
  color: ${({ $checked, $isWeekend }) => ($checked ? '#fff' : $isWeekend ? '#ff4d4f' : '#595959')};
  font-size: 16px;
  user-select: none;

  &:hover {
    border-color: #1890ff;
    color: ${({ $checked }) => ($checked ? '#fff' : '#1890ff')};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  ${({ $checked }) =>
    $checked &&
    css`
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
    `}
`;

const ActionButton = styled.div<{ $checked: boolean; $color: string }>`
  box-sizing: border-box;
  padding: 10px 14px;
  border-radius: 10px;
  border: 2px solid ${({ $checked, $color }) => ($checked ? $color : '#f0f0f0')};
  background: ${({ $checked, $color }) => ($checked ? `${$color}10` : '#fff')};
  color: ${({ $checked, $color }) => ($checked ? $color : '#595959')};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  width: 100%;
  min-height: 44px;

  &:hover {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .icon-wrapper {
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: ${({ $checked, $color }) => ($checked ? $color : '#f5f5f5')};
    color: ${({ $checked, $color }) => ($checked ? '#fff' : '#8c8c8c')};
    transition: all 0.2s;
    flex-shrink: 0;
  }
  
  &:hover .icon-wrapper {
    background: ${({ $color }) => $color};
    color: #fff;
  }
`;

const normalizeDays = (input?: number[] | null) => {
  if (!Array.isArray(input)) return DEFAULT_DAYS;
  const set = new Set<number>();
  input.forEach((v) => {
    if (Number.isInteger(v) && v >= 0 && v <= 6) set.add(v);
  });
  return Array.from(set).sort((a, b) => a - b);
};

const normalizeActions = (input?: SlackNotifyAction[] | null) => {
  if (!Array.isArray(input)) return DEFAULT_ACTIONS;
  const set = new Set<SlackNotifyAction>(input);
  return ACTION_OPTIONS.map(opt => opt.value).filter(v => set.has(v));
};

const isSameArray = <T,>(a: T[], b: T[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const SlackNotifySettingsPage: React.FC = () => {
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState<number[]>(DEFAULT_DAYS);
  const [actions, setActions] = useState<SlackNotifyAction[]>(DEFAULT_ACTIONS);
  const [initialDays, setInitialDays] = useState<number[]>(DEFAULT_DAYS);
  const [initialActions, setInitialActions] = useState<SlackNotifyAction[]>(DEFAULT_ACTIONS);

  useEffect(() => {
    const repo = new AccountRepositoryImpl();
    const uc = new GetSlackNotifySettingsUseCase(repo);
    setLoading(true);
    uc.execute()
      .then((res) => {
        const nextDays = normalizeDays(res.slack_notify_days);
        const nextActions = normalizeActions(res.slack_notify_actions);
        setDays(nextDays);
        setActions(nextActions);
        setInitialDays(nextDays);
        setInitialActions(nextActions);
      })
      .catch(() => {
        message.error('Slack 알림 설정을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(
    () => !isSameArray(days, initialDays) || !isSameArray(actions, initialActions),
    [days, actions, initialDays, initialActions],
  );

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const repo = new AccountRepositoryImpl();
      const uc = new UpdateSlackNotifySettingsUseCase(repo);
      const res = await uc.execute({
        slack_notify_days: days,
        slack_notify_actions: actions,
      });
      const nextDays = normalizeDays(res.slack_notify_days);
      const nextActions = normalizeActions(res.slack_notify_actions);
      setDays(nextDays);
      setActions(nextActions);
      setInitialDays(nextDays);
      setInitialActions(nextActions);
      message.success('Slack 알림 설정이 저장되었습니다.');
    } catch {
      message.error('Slack 알림 설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [days, actions, saving]);

  const handleReset = useCallback(() => {
    setDays(initialDays);
    setActions(initialActions);
    message.info('변경사항을 초기화했습니다.');
  }, [initialDays, initialActions]);

  const handleSelectAllDays = useCallback(() => {
    setDays(DEFAULT_DAYS);
  }, []);

  const handleClearDays = useCallback(() => {
    setDays([]);
  }, []);

  const handleSelectAllActions = useCallback(() => {
    setActions(DEFAULT_ACTIONS);
  }, []);

  const handleClearActions = useCallback(() => {
    setActions([]);
  }, []);

  const handleToggleDay = useCallback((value: number) => {
    setDays((prev) => {
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      return normalizeDays(next);
    });
  }, []);

  const handleToggleAction = useCallback((value: SlackNotifyAction) => {
    setActions((prev) => {
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      return normalizeActions(next);
    });
  }, []);

  const username = user?.username ?? 'adpa99';

  if (loading) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <Spin size="large" tip="설정을 불러오는 중..." />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 헤더 섹션 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={2} style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <SlackOutlined /> Slack 로그 알림 설정
            </Title>
            <Paragraph type="secondary" style={{ fontSize: 16 }}>
              어떤 활동에 대해 알림을 받을지 상세하게 설정할 수 있습니다.
            </Paragraph>
          </div>
          <Space>
            <Button
              size="large"
              icon={<UndoOutlined />}
              onClick={handleReset}
              disabled={!isDirty || saving}
            >
              되돌리기
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty}
            >
              저장하기
            </Button>
          </Space>
        </div>

        {/* 요약 카드 */}
        <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} md={12}>
              <Space direction="vertical" size={4}>
                <Text type="secondary">알림 수신 계정</Text>
                <Space align="center">
                  <Title level={4} style={{ margin: 0 }}>{username}</Title>
                  <Tag color="blue" style={{ borderRadius: 12 }}>관리자</Tag>
                </Space>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ background: '#f8f9fa', padding: '16px 24px', borderRadius: 12 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 13 }}>선택된 요일</Text>
                    <div style={{ fontSize: 20, fontWeight: 700, color: days.length > 0 ? '#1890ff' : '#bfbfbf' }}>
                      {days.length} <span style={{ fontSize: 14, fontWeight: 400, color: '#8c8c8c' }}>/ 7일</span>
                    </div>
                  </Col>
                  <Col span={12} style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 24 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>선택된 유형</Text>
                    <div style={{ fontSize: 20, fontWeight: 700, color: actions.length > 0 ? '#52c41a' : '#bfbfbf' }}>
                      {actions.length} <span style={{ fontSize: 14, fontWeight: 400, color: '#8c8c8c' }}>/ 5개</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 설정 섹션 */}
        <Row gutter={[24, 24]}>
          {/* 요일 설정 */}
          <Col xs={24} lg={10}>
            <Card
              title={<span style={{ fontSize: 18, fontWeight: 600 }}><CalendarOutlined style={{ marginRight: 8 }} />요일 설정</span>}
              style={{ height: '100%', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
              headStyle={{ borderBottom: '1px solid #f0f0f0' }}
              extra={
                <Space size={4}>
                  <Button type="text" size="small" onClick={handleSelectAllDays}>전체</Button>
                  <Divider type="vertical" />
                  <Button type="text" size="small" onClick={handleClearDays} danger>해제</Button>
                </Space>
              }
            >
              <div style={{ padding: '12px 0' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                  알림을 받고 싶은 요일을 선택하세요. (주말 포함)
                </Text>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                  {DAY_OPTIONS.map((opt) => {
                    const checked = days.includes(opt.value);
                    return (
                      <DayButton
                        key={opt.value}
                        $checked={checked}
                        $isWeekend={opt.isWeekend}
                        onClick={() => handleToggleDay(opt.value)}
                      >
                        {opt.label}
                      </DayButton>
                    );
                  })}
                </div>

                {days.length === 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="선택된 요일이 없습니다."
                    description="어떤 요일에도 알림이 발송되지 않습니다."
                    style={{ marginTop: 32, borderRadius: 8 }}
                  />
                )}
              </div>
            </Card>
          </Col>

          {/* 유형 설정 */}
          <Col xs={24} lg={14}>
            <Card
              title={<span style={{ fontSize: 18, fontWeight: 600 }}><BellOutlined style={{ marginRight: 8 }} />유형 설정</span>}
              style={{ height: '100%', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
              headStyle={{ borderBottom: '1px solid #f0f0f0' }}
              extra={
                <Space size={4}>
                  <Button type="text" size="small" onClick={handleSelectAllActions}>전체</Button>
                  <Divider type="vertical" />
                  <Button type="text" size="small" onClick={handleClearActions} danger>해제</Button>
                </Space>
              }
            >
              <div style={{ padding: '12px 0' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                  알림을 받고 싶은 활동 유형을 선택하세요.
                </Text>

                <Row gutter={[12, 12]}>
                  {ACTION_OPTIONS.map((opt) => {
                    const checked = actions.includes(opt.value);
                    const meta = ACTION_META[opt.value];
                    return (
                      <Col span={12} key={opt.value}>
                        <ActionButton
                          $checked={checked}
                          $color={meta.color}
                          onClick={() => handleToggleAction(opt.value)}
                        >
                          <Space>
                            <div className="icon-wrapper">
                              {meta.icon}
                            </div>
                            <span>{opt.label}</span>
                          </Space>
                          {checked && <CheckOutlined style={{ color: meta.color }} />}
                        </ActionButton>
                      </Col>
                    );
                  })}
                </Row>

                {actions.length === 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="선택된 유형이 없습니다."
                    description="어떤 활동에도 알림이 발송되지 않습니다."
                    style={{ marginTop: 32, borderRadius: 8 }}
                  />
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default SlackNotifySettingsPage;
